/**
 * AdminSubjects — admin can add/edit/delete subjects per class.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus, Pencil, Trash2, Save, X, AlertTriangle, Loader2, Sparkles, ArrowRight, ChevronDown, Tags, FileText, CheckCircle2, ListPlus, Upload, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

export default function AdminSubjects({ embedded } = {}) {
  const [cls, setCls] = useState("5");
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);   // {id, name}
  const [editName, setEditName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // subject row pending delete

  // ----- Manage lessons per subject -----
  const [lessonsSubject, setLessonsSubject] = useState(null); // subject whose lessons dialog is open
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [newLessonTitle, setNewLessonTitle] = useState("");
  const [addingLesson, setAddingLesson] = useState(false);
  const [busyLessonId, setBusyLessonId] = useState(null); // lesson currently updating/deleting
  const [editLessonId, setEditLessonId] = useState(null);
  const [editLessonTitle, setEditLessonTitle] = useState("");

  // ----- LBA (Lesson-Based-Assessment) PDF per lesson -----
  const lbaInputRef = useRef(null);
  const lbaTargetRef = useRef(null);
  const [lbaBusyId, setLbaBusyId] = useState(null);

  const pickLba = (lesson) => { lbaTargetRef.current = lesson.id; lbaInputRef.current?.click(); };

  const onLbaFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const lessonId = lbaTargetRef.current;
    if (!file || !lessonId) return;
    if (file.type !== "application/pdf") { toast.error("Please choose a PDF file."); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("PDF too large (max 25 MB)."); return; }
    setLbaBusyId(lessonId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tuition/library/lessons/${lessonId}/lba`, fd);
      toast.success("LBA PDF uploaded");
      await fetchLessons(lessonsSubject.id);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not upload LBA PDF");
    } finally { setLbaBusyId(null); }
  };

  const removeLba = async (lesson) => {
    setLbaBusyId(lesson.id);
    try {
      await api.delete(`/tuition/library/lessons/${lesson.id}/lba`);
      toast.success("LBA PDF removed");
      await fetchLessons(lessonsSubject.id);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not remove LBA PDF");
    } finally { setLbaBusyId(null); }
  };

  const downloadLba = async (lesson) => {
    try {
      const res = await api.get(`/tuition/library/lessons/${lesson.id}/lba`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not open LBA PDF");
    }
  };

  const fetchLessons = async (subjectId) => {
    setLessonsLoading(true);
    try {
      const { data } = await api.get("/tuition/library", { params: { class_name: cls } });
      const subj = (data?.subjects || []).find((s) => s.id === subjectId);
      setLessons(subj?.lessons || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load lessons");
      setLessons([]);
    } finally { setLessonsLoading(false); }
  };

  const openLessons = (s) => { setLessonsSubject(s); setNewLessonTitle(""); fetchLessons(s.id); };

  const addLesson = async () => {
    const title = newLessonTitle.trim();
    if (title.length < 2) { toast.error("Lesson title must be at least 2 characters"); return; }
    setAddingLesson(true);
    try {
      await api.post("/tuition/library/lessons", { subject_id: lessonsSubject.id, title });
      toast.success(`Added lesson "${title}"`);
      setNewLessonTitle("");
      await fetchLessons(lessonsSubject.id);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not add lesson");
    } finally { setAddingLesson(false); }
  };

  const deleteLesson = async (lesson) => {
    setBusyLessonId(lesson.id);
    try {
      await api.delete(`/tuition/library/lessons/${lesson.id}`);
      toast.success("Lesson removed");
      await fetchLessons(lessonsSubject.id);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete lesson");
    } finally { setBusyLessonId(null); }
  };

  const saveLessonEdit = async (lesson) => {
    const title = editLessonTitle.trim();
    if (title.length < 2) { toast.error("Title must be at least 2 characters"); return; }
    setBusyLessonId(lesson.id);
    try {
      await api.patch(`/tuition/library/lessons/${lesson.id}`, { title });
      toast.success("Lesson updated");
      setEditLessonId(null); setEditLessonTitle("");
      await fetchLessons(lessonsSubject.id);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update lesson");
    } finally { setBusyLessonId(null); }
  };

  // ----- Subject name aliases (admin-editable canonical map) -----
  const [aliases, setAliases] = useState({ custom: [], defaults: [], canonical_options: [] });
  const [aliasLoading, setAliasLoading] = useState(false);
  const [newAlias, setNewAlias] = useState("");
  const [newCanonical, setNewCanonical] = useState("");
  const [addingAlias, setAddingAlias] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);

  const loadAliases = async () => {
    setAliasLoading(true);
    try {
      const { data } = await api.get("/admin/subject-aliases");
      setAliases(data || { custom: [], defaults: [], canonical_options: [] });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load aliases");
    } finally { setAliasLoading(false); }
  };

  useEffect(() => { loadAliases(); /* eslint-disable-next-line */ }, []);

  const createAlias = async () => {
    const alias = newAlias.trim();
    const canonical = newCanonical.trim();
    if (alias.length < 2) { toast.error("Alias must be at least 2 characters"); return; }
    if (!canonical) { toast.error("Pick the canonical subject name"); return; }
    setAddingAlias(true);
    try {
      await api.post("/admin/subject-aliases", { alias, canonical });
      toast.success(`"${alias}" now maps to ${canonical}`);
      setNewAlias(""); setNewCanonical("");
      await loadAliases();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not add alias");
    } finally { setAddingAlias(false); }
  };

  const deleteAlias = async (id) => {
    try {
      await api.delete(`/admin/subject-aliases/${id}`);
      toast.success("Alias removed");
      await loadAliases();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete alias");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subjects", { params: { class_name: cls } });
      setSubjects(data || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load subjects");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cls]);

  const create = async () => {
    const name = newName.trim();
    if (name.length < 2) { toast.error("Name must be at least 2 characters"); return; }
    setCreating(true);
    try {
      await api.post("/admin/subjects", { class_name: cls, name });
      toast.success(`Added ${name}`);
      setNewName("");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not add");
    } finally { setCreating(false); }
  };

  // Feb 2026: Hindi is available to every student in Classes 1-10 as an
  // OPTIONAL (grade-only, non-mandatory) subject. Server-side is idempotent —
  // clicking again after an already-seeded class is a no-op.
  const [seedingHindi, setSeedingHindi] = useState(false);
  const seedHindi = async () => {
    setSeedingHindi(true);
    try {
      const { data } = await api.post("/admin/subjects/seed-hindi");
      const n = (data.seeded_classes || []).length;
      if (n > 0) toast.success(`Hindi added to ${n} class${n === 1 ? "" : "es"} (1-10).`);
      else toast.message("Hindi already exists for all classes 1-10.");
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not seed Hindi");
    } finally { setSeedingHindi(false); }
  };

  const saveEdit = async () => {
    const name = editName.trim();
    if (!editing || name.length < 2) { toast.error("Name must be at least 2 characters"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/admin/subjects/${editing.id}`, { name });
      toast.success("Subject renamed (historical marks were re-tagged too)");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not rename");
    } finally { setSavingEdit(false); }
  };

  const doDelete = async (force = false) => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/subjects/${confirmDel.id}`, { params: force ? { force: true } : {} });
      toast.success(`Deleted ${confirmDel.name}`);
      setConfirmDel(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && !force) {
        // Re-prompt with force option
        setConfirmDel({ ...confirmDel, _needsForce: true, _conflictMsg: detail });
        return;
      }
      toast.error(apiErr(detail) || "Could not delete");
    }
  };

  const [busyMandatory, setBusyMandatory] = useState(null);
  const toggleMandatory = async (s) => {
    setBusyMandatory(s.id);
    const next = !s.mandatory;
    setSubjects((prev) => prev.map((x) => x.id === s.id ? { ...x, mandatory: next } : x));
    try {
      await api.patch(`/admin/subjects/${s.id}/mandatory`, { mandatory: next });
      toast.success(next ? `${s.name} is now mandatory (counts for marks & report cards)` : `${s.name} removed from mandatory`);
    } catch (e) {
      setSubjects((prev) => prev.map((x) => x.id === s.id ? { ...x, mandatory: !next } : x));
      toast.error(apiErr(e.response?.data?.detail) || "Could not update");
    } finally { setBusyMandatory(null); }
  };

  // Feb 2026: per-subject "Marks card" toggle — controls whether this subject
  // shows on I/II Term report cards. Default ON (`in_marks_card !== false`)
  // so legacy subjects keep appearing until admin explicitly hides one.
  const [busyMarksCard, setBusyMarksCard] = useState(null);
  const toggleInMarksCard = async (s) => {
    setBusyMarksCard(s.id);
    const current = s.in_marks_card !== false;
    const next = !current;
    setSubjects((prev) => prev.map((x) => x.id === s.id ? { ...x, in_marks_card: next } : x));
    try {
      await api.patch(`/admin/subjects/${s.id}/marks-card`, { in_marks_card: next });
      toast.success(next
        ? `${s.name} will now appear on Term report cards`
        : `${s.name} hidden from Term report cards (still shown in AI Tuitions)`);
    } catch (e) {
      setSubjects((prev) => prev.map((x) => x.id === s.id ? { ...x, in_marks_card: current } : x));
      toast.error(apiErr(e.response?.data?.detail) || "Could not update");
    } finally { setBusyMarksCard(null); }
  };

  const totalLessons = useMemo(() => subjects.reduce((a, s) => a + (s.lesson_count || 0), 0), [subjects]);

  const Wrap = embedded ? "div" : Layout;
  return (
    <Wrap>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Admin · Curriculum</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>
            <BookOpen className="inline h-7 w-7 text-emerald-500 mr-2 -mt-1" />
            Subjects per class
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Define the curriculum subjects for each class. Edits cascade through Assessments, Results and the AI Tuitions library.</p>
          <div className="mt-3">
            <Button
              onClick={seedHindi}
              disabled={seedingHindi}
              variant="outline"
              size="sm"
              className="rounded-full border-amber-400 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
              title="Add Hindi (grade-only, non-mandatory) to every class from 1 to 10. Safe to click again — already-seeded classes are skipped."
              data-testid="subjects-seed-hindi-btn"
            >
              {seedingHindi ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Add Hindi to all classes (1-10)
            </Button>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5 mb-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
              <Select value={cls} onValueChange={setCls}>
                <SelectTrigger data-testid="subjects-class-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {CLASSES.map((c) => <SelectItem key={c} value={c} data-testid={`subjects-class-opt-${c}`}>{["LKG", "UKG", "Play Group"].includes(c) ? c : `Class ${c}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-[2] min-w-[220px]">
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Add new subject</label>
              <div className="flex gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Hindi, Computer Science…"
                  onKeyDown={(e) => e.key === "Enter" && create()}
                  data-testid="subjects-new-input"
                  maxLength={60}
                />
                <Button onClick={create} disabled={creating || !newName.trim()} className="bg-emerald-500 hover:bg-emerald-600 shrink-0" data-testid="subjects-new-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin"/> : <Plus className="h-4 w-4 mr-1"/>}
                  Add
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-wrap gap-3 text-xs uppercase tracking-[0.15em] text-slate-500">
            <span>{["LKG", "UKG", "Play Group"].includes(cls) ? cls : `Class ${cls}`} · {subjects.length} subjects</span>
            <span className="normal-case tracking-normal text-slate-400">{totalLessons} lesson{totalLessons === 1 ? "" : "s"} indexed</span>
          </div>
          {loading ? (
            <div className="py-20 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin"/></div>
          ) : subjects.length === 0 ? (
            <div className="py-16 text-center text-slate-500" data-testid="subjects-empty">
              <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No subjects yet. Add one above.
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {[...subjects].sort((a, b) => (b.mandatory === a.mandatory ? String(a.name).localeCompare(String(b.name)) : (b.mandatory ? 1 : -1))).map((s) => (
                <div key={s.id} className="px-5 py-3 flex items-center gap-3" data-testid={`subject-row-${s.id}`}>
                  <div className="h-9 w-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 truncate flex items-center gap-2">
                      {s.name}
                      {s.mandatory && <span className="inline-block text-[9px] uppercase tracking-wider font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded" data-testid={`subject-mandatory-badge-${s.id}`}>Mandatory</span>}
                      {s.grade_only && <span className="inline-block text-[9px] uppercase tracking-wider font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded" data-testid={`subject-grade-only-badge-${s.id}`}>Grade only · default A</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {s.lesson_count > 0 ? `${s.lesson_count} lesson${s.lesson_count === 1 ? "" : "s"} in AI Tuition library` : "No tuition lessons yet"}
                      {s.is_default && <span className="ml-2 inline-block text-[9px] uppercase tracking-wider font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 px-1.5 py-0.5 rounded">Default</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => toggleMandatory(s)} disabled={busyMandatory === s.id} className={`h-8 ${s.mandatory ? "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"}`} data-testid={`subject-mandatory-${s.id}`} title="Mandatory subjects are the only ones shown for marks entry and on report cards">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1"/> {s.mandatory ? "Mandatory" : "Mark mandatory"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleInMarksCard(s)}
                    disabled={busyMarksCard === s.id}
                    className={`h-8 ${s.in_marks_card !== false ? "text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30" : "text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"}`}
                    data-testid={`subject-marks-card-${s.id}`}
                    title="Show this subject on I/II Term report cards (Marks Card entry). Toggle off to keep the subject in AI Tuitions but hide it from report cards."
                  >
                    <FileCheck2 className="h-3.5 w-3.5 mr-1"/> {s.in_marks_card !== false ? "On marks card" : "Add to marks card"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openLessons(s)} className="h-8 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" data-testid={`subject-lessons-${s.id}`}>
                    <ListPlus className="h-3.5 w-3.5 mr-1"/> Lessons
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(s); setEditName(s.name); }} className="h-8 text-slate-500 hover:text-sky-600" data-testid={`subject-edit-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1"/> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDel(s)} className="h-8 text-slate-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" data-testid={`subject-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1"/> Delete
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Subject name aliases ---- */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5 mt-6" data-testid="aliases-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-violet-50 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 flex items-center justify-center shrink-0">
              <Tags className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Subject name aliases</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Map alternative spellings (e.g. <span className="font-mono">maths</span>, <span className="font-mono">sst</span>) to a single canonical subject. Used when importing teachers and matching timetables — so a new spelling never breaks the app.
              </p>
            </div>
          </div>

          {/* Add alias */}
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Alias / spelling</label>
              <Input value={newAlias} onChange={(e) => setNewAlias(e.target.value)} placeholder="e.g. bio, comp sci, p.e." maxLength={60} onKeyDown={(e) => e.key === "Enter" && createAlias()} data-testid="alias-new-input" />
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400 mb-3 hidden sm:block" />
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Maps to (canonical)</label>
              <Select value={newCanonical} onValueChange={setNewCanonical}>
                <SelectTrigger data-testid="alias-canonical-select"><SelectValue placeholder="Choose subject…" /></SelectTrigger>
                <SelectContent>
                  {(aliases.canonical_options || []).map((c) => <SelectItem key={c} value={c} data-testid={`alias-canonical-opt-${c}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={createAlias} disabled={addingAlias || !newAlias.trim() || !newCanonical} className="bg-violet-500 hover:bg-violet-600 shrink-0" data-testid="alias-add-btn">
              {addingAlias ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add alias
            </Button>
          </div>

          {/* Custom aliases */}
          {aliasLoading ? (
            <div className="py-6 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
          ) : (aliases.custom || []).length === 0 ? (
            <div className="text-sm text-slate-400 py-3" data-testid="aliases-empty">No custom aliases yet. The built-in defaults below are always active.</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="aliases-custom-list">
              {aliases.custom.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-violet-50 dark:bg-violet-900/30 border border-violet-100 dark:border-violet-800/50 text-sm" data-testid={`alias-chip-${a.id}`}>
                  <span className="font-mono text-violet-700 dark:text-violet-300">{a.alias}</span>
                  <ArrowRight className="h-3 w-3 text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-200">{a.canonical}</span>
                  <button onClick={() => deleteAlias(a.id)} className="ml-1 h-5 w-5 rounded-full hover:bg-rose-100 dark:hover:bg-rose-900/40 text-slate-400 hover:text-rose-600 flex items-center justify-center" data-testid={`alias-delete-${a.id}`} title="Remove alias">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Built-in defaults (read-only) */}
          <button onClick={() => setShowDefaults((v) => !v)} className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" data-testid="aliases-toggle-defaults">
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDefaults ? "rotate-180" : ""}`} />
            Built-in defaults ({(aliases.defaults || []).length})
          </button>
          {showDefaults && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="aliases-default-list">
              {aliases.defaults.map((a) => (
                <span key={a.alias} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700/60 text-xs text-slate-500 dark:text-slate-300">
                  <span className="font-mono">{a.alias}</span>
                  <ArrowRight className="h-2.5 w-2.5" />
                  {a.canonical}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="subject-edit-dialog">
          <DialogHeader><DialogTitle>Rename subject</DialogTitle><DialogDescription>Enter a new name for this subject.</DialogDescription></DialogHeader>
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit()} data-testid="subject-edit-input" maxLength={60} />
          <div className="text-xs text-slate-500">Existing assessment marks tagged with the old name will be migrated to the new name automatically.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit || !editName.trim()} className="bg-sky-500 hover:bg-sky-600" data-testid="subject-edit-save">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4 mr-1"/>}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="subject-delete-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-500"/>Delete &quot;{confirmDel?.name}&quot;?</DialogTitle><DialogDescription>This permanently removes the subject and its lessons. This action cannot be undone.</DialogDescription></DialogHeader>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            {confirmDel?._needsForce
              ? <>This subject has <b>{confirmDel.lesson_count} lesson(s)</b> in the AI Tuition library. Continuing will permanently delete the lessons and their indexed chunks.</>
              : <>This will remove the subject from {confirmDel?.class_name ? `class ${confirmDel.class_name}` : "this class"}. Past assessment marks tagged with this subject will stay in the database but won&apos;t show up in dropdowns.</>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button onClick={() => doDelete(!!confirmDel?._needsForce)} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="subject-delete-confirm">
              <Trash2 className="h-4 w-4 mr-1.5"/>
              {confirmDel?._needsForce ? "Yes, delete subject + lessons" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage lessons */}
      <Dialog open={!!lessonsSubject} onOpenChange={(v) => !v && setLessonsSubject(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="subject-lessons-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <ListPlus className="h-5 w-5 text-emerald-500" /> Lessons · {lessonsSubject?.name}
            </DialogTitle>
            <DialogDescription>Add, reorder or remove lessons for this subject.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">
            Add, rename or remove lessons for this subject. Upload the lesson PDF in the AI Tuition Library. Use the <b>Upload</b> icon on a lesson to attach its <b>LBA (Lesson-Based-Assessment)</b> PDF — a downloadable file for teachers, not fed to the AI.
          </p>
          <input ref={lbaInputRef} type="file" accept="application/pdf" className="hidden" onChange={onLbaFile} data-testid="lba-file-input" />

          {/* Add lesson */}
          <div className="flex gap-2 mt-1">
            <Input
              value={newLessonTitle}
              onChange={(e) => setNewLessonTitle(e.target.value)}
              placeholder="New lesson title, e.g. Chapter 3 — Fractions"
              onKeyDown={(e) => e.key === "Enter" && addLesson()}
              maxLength={140}
              data-testid="lesson-new-input"
            />
            <Button onClick={addLesson} disabled={addingLesson || !newLessonTitle.trim()} className="bg-emerald-500 hover:bg-emerald-600 shrink-0" data-testid="lesson-add-btn">
              {addingLesson ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add
            </Button>
          </div>

          {/* Lessons list */}
          <div className="mt-2 max-h-[46vh] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
            {lessonsLoading ? (
              <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
            ) : lessons.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm" data-testid="lessons-empty">No lessons yet. Add one above.</div>
            ) : lessons.map((ln) => (
              <div key={ln.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`lesson-row-${ln.id}`}>
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  {editLessonId === ln.id ? (
                    <div className="flex gap-2">
                      <Input value={editLessonTitle} onChange={(e) => setEditLessonTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveLessonEdit(ln)} maxLength={140} className="h-8" autoFocus data-testid={`lesson-edit-input-${ln.id}`} />
                      <Button size="sm" className="h-8 bg-emerald-500 hover:bg-emerald-600 shrink-0" disabled={busyLessonId === ln.id} onClick={() => saveLessonEdit(ln)} data-testid={`lesson-edit-save-${ln.id}`}>
                        {busyLessonId === ln.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 shrink-0" onClick={() => { setEditLessonId(null); setEditLessonTitle(""); }}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{ln.title}</div>
                      <div className="text-xs mt-0.5">
                        {ln.indexed ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Indexed{ln.filename ? ` · ${ln.filename}` : ""}</span>
                        ) : (
                          <span className="text-slate-400">No PDF — add it in the Tuition Library</span>
                        )}
                      </div>
                      <div className="text-xs mt-1">
                        {ln.lba_file_id ? (
                          <button type="button" onClick={() => downloadLba(ln)} className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400 hover:underline" data-testid={`lba-download-${ln.id}`}>
                            <FileCheck2 className="h-3 w-3" /> LBA: {ln.lba_filename || "assessment.pdf"}
                          </button>
                        ) : (
                          <span className="text-slate-400">No LBA assessment PDF</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {editLessonId !== ln.id && (
                  <>
                    <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-emerald-600" disabled={lbaBusyId === ln.id} onClick={() => pickLba(ln)} title={ln.lba_file_id ? "Replace LBA PDF" : "Upload LBA PDF"} data-testid={`lba-upload-${ln.id}`}>
                      {lbaBusyId === ln.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    </Button>
                    {ln.lba_file_id && (
                      <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-rose-600" disabled={lbaBusyId === ln.id} onClick={() => removeLba(ln)} title="Remove LBA PDF" data-testid={`lba-remove-${ln.id}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-sky-600" disabled={busyLessonId === ln.id} onClick={() => { setEditLessonId(ln.id); setEditLessonTitle(ln.title); }} data-testid={`lesson-edit-${ln.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-rose-600" disabled={busyLessonId === ln.id} onClick={() => deleteLesson(ln)} data-testid={`lesson-delete-${ln.id}`}>
                      {busyLessonId === ln.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonsSubject(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Wrap>
  );
}
